import { ReactNode, useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../stores/auth'
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Users2,
  Mail,
  Settings,
  LogOut,
  BarChart3,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react'

const nav = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/events', icon: CalendarDays, label: 'Events' },
  { to: '/guests', icon: Users, label: 'G\u00e4ste' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/campaigns', icon: Mail, label: 'Kampagnen' },
  { to: '/staff', icon: Users2, label: 'Mitarbeiter' },
  { to: '/settings', icon: Settings, label: 'Einstellungen' },
]

export function Layout({ children }: { children: ReactNode }) {
  const { org, staff, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Close sidebar on click outside (mobile)
  useEffect(() => {
    if (!sidebarOpen) return
    const handleClick = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setSidebarOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [sidebarOpen])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [sidebarOpen])

  const sidebarContent = (
    <>
      {/* Club Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          {org?.logo_url ? (
            <img src={org.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center">
              <span className="text-xs font-bold tracking-wider" style={{ background: 'linear-gradient(135deg, #c8c8d0, #8a8a96, #c8c8d0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>V</span>
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{org?.name || 'VNVLD'}</p>
            <p className="text-xs text-[#b0aeb8] truncate">{org?.slug}.unvld.me</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav aria-label="Hauptnavigation" className="flex-1 p-3 space-y-1">
        {nav.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'bg-white/10 text-[#e8e8ec] font-medium'
                  : 'text-[#b0aeb8] hover:text-white hover:bg-white/5'
              }`
            }
          >
            <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User / Logout */}
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{staff?.display_name}</p>
            <p className="text-xs text-[#707078] capitalize">{staff?.role}</p>
          </div>
          <button
            onClick={() => { logout(); navigate('/login') }}
            className="p-2 text-[#707078] hover:text-red-400 transition-colors"
            title="Abmelden"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Mobile Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 md:hidden bg-[#131316] border-b border-white/5">
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 text-[#b0aeb8] hover:text-white transition-colors"
            aria-label="Men\u00fc \u00f6ffnen"
          >
            <Menu className="w-5 h-5" aria-hidden="true" />
          </button>
          <p className="text-sm font-semibold text-white">{org?.name || 'VNVLD'}</p>
          <div className="w-9" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          {/* Sidebar */}
          <aside
            ref={sidebarRef}
            aria-label="Seitenleiste"
            className="absolute top-0 left-0 bottom-0 w-64 bg-[#131316] border-r border-white/5 flex flex-col animate-slide-in"
          >
            {/* Close button */}
            <div className="absolute top-3 right-3">
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-[#707078] hover:text-white transition-colors"
                aria-label="Men\u00fc schlie\u00dfen"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside aria-label="Seitenleiste" className="hidden md:flex w-64 bg-[#131316] border-r border-white/5 flex-col flex-shrink-0">
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-4 pt-[calc(3.5rem+1rem)] md:p-6 md:pt-6">
          {children}
        </div>
      </main>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.2s ease-out;
        }
      `}</style>
    </div>
  )
}
